
import React, { useState, useMemo, useEffect } from 'react';
import { VendorProfile, VendorStatus, SubscriptionPlan, Neighborhood, Category, Ad, UserRole, SystemConfig, ManagementRole, StaffMember, Promotion, PromotionType, ActivationRule } from '../types';
import VendorPreviewModal from './VendorPreviewModal';
import RequestChangesModal from './RequestChangesModal';
import QuickMessageModal from './QuickMessageModal';
import MetricCard from './MetricCard';
import { calculateCurrentMetrics } from '../services/metricsService';
import Logo from './Logo';
import { supabase } from '../lib/supabase';

interface AdminPanelProps {
  vendors: VendorProfile[];
  ads: Ad[];
  neighborhoods: Neighborhood[];
  categories: Category[];
  systemConfig: SystemConfig;
  promotions: Promotion[];
  managementRole?: ManagementRole;
  staffList: StaffMember[];
  currentUserId?: string; 
  onUpdateConfig: (config: SystemConfig) => void;
  onSaveConfig: () => void; 
  onSavePromotion: (promo: Partial<Promotion>) => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onUpdatePlan: (id: string, plan: SubscriptionPlan, daysOrDate?: number | string) => void;
  onUpdateVerificationBadge: (id: string, days: number) => void; 
  onUpdateVendorCategory: (vendorId: string, categoryId: string) => void;
  onAddNeighborhood: (name: string, isAirport: boolean) => void;
  onUpdateNeighborhood: (id: string, name: string, isAirport: boolean) => void;
  onAddCategory: (name: string, icon?: string) => void;
  onUpdateCategory: (id: string, name: string, icon?: string) => void;
  onUpdateStaff: () => void;
  onBack: () => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ 
  vendors, ads, neighborhoods, categories, systemConfig, promotions = [], managementRole = ManagementRole.MODERATOR, staffList, currentUserId, onUpdateConfig, onSaveConfig, onSavePromotion, onApprove, onReject, onUpdatePlan, onUpdateVerificationBadge, onUpdateVendorCategory, onAddNeighborhood, onUpdateNeighborhood, onAddCategory, onUpdateCategory, onUpdateStaff, onBack
}) => {
  const [tab, setTab] = useState<'kpis' | 'vendedores' | 'campanhas' | 'renovacoes' | 'equipe' | 'taxonomias' | 'config' | 'perfil'>('kpis');
  const [adminSearch, setAdminSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<VendorStatus | 'ALL'>('ALL');
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [renewalVendorId, setRenewalVendorId] = useState<string | null>(null);
  const [messageVendorId, setMessageVendorId] = useState<string | null>(null);
  const [requestVendor, setRequestVendor] = useState<VendorProfile | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString());
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null);

  // States para Promoções
  const [isAddingPromo, setIsAddingPromo] = useState(false);
  const [promoWizardStep, setPromoWizardStep] = useState(1);
  const [newPromo, setNewPromo] = useState<Partial<Promotion>>({
    name: '',
    benefit_type: PromotionType.PRO_UPGRADE,
    benefit_value: 30,
    activation_rule: ActivationRule.NEW_SIGNUP,
    is_active: true
  });

  const me = useMemo(() => staffList.find(s => s.id === currentUserId), [staffList, currentUserId]);
  const [profileForm, setProfileForm] = useState({ name: '', whatsapp: '' });
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Estados para edição de bairros e categorias
  const [newNeighName, setNewNeighName] = useState('');
  const [isAirport, setIsAirport] = useState(false);
  const [editingNeighId, setEditingNeighId] = useState<string | null>(null);
  const [neighEditName, setNeighEditName] = useState('');

  const [newCatName, setNewCatName] = useState('');
  const [newCatIcon, setNewCatIcon] = useState('📁');
  const [editingCatId, setEditingCatId] = useState<string | null>(null);
  const [catEditName, setCatEditName] = useState('');
  const [catEditIcon, setCatEditIcon] = useState('📁');

  useEffect(() => {
    if (me) {
      setProfileForm({ name: me.name, whatsapp: me.whatsapp });
    }
  }, [me]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date().toLocaleTimeString()), 1000);
    return () => clearInterval(timer);
  }, []);

  const showNotify = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUserId) return;
    
    if (!profileForm.name.trim()) {
      showNotify("O nome não pode estar vazio", "error");
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase.from('equipe_gestao').update({
        nome: profileForm.name.trim(),
        whatsapp: profileForm.whatsapp.replace(/\D/g, '')
      }).eq('id', currentUserId);

      if (error) {
        console.error("Erro Supabase (Profile Update):", error);
        showNotify(`Erro: ${error.message || "Não foi possível atualizar o perfil"}`, "error");
      } else {
        showNotify("Perfil atualizado com sucesso!");
        onUpdateStaff();
      }
    } catch (err) {
      console.error("Erro inesperado no Profile Update:", err);
      showNotify("Ocorreu um erro de conexão", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!me || !currentUserId) return;
    
    if (passwordForm.current !== me.password) {
      showNotify("Senha atual incorreta", "error");
      return;
    }
    if (passwordForm.new.length < 6) {
      showNotify("A nova senha deve ter no mínimo 6 caracteres", "error");
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      showNotify("As novas senhas não coincidem", "error");
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase.from('equipe_gestao').update({
        password: passwordForm.new
      }).eq('id', currentUserId);

      if (error) {
        console.error("Erro Supabase (Password Update):", error);
        showNotify(`Erro: ${error.message || "Não foi possível alterar a senha"}`, "error");
      } else {
        showNotify("Senha alterada com sucesso!");
        setPasswordForm({ current: '', new: '', confirm: '' });
        onUpdateStaff();
      }
    } catch (err) {
      console.error("Erro inesperado no Password Update:", err);
      showNotify("Ocorreu um erro ao tentar mudar a senha", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [staffForm, setStaffForm] = useState<Partial<StaffMember>>({ name: '', email: '', whatsapp: '', password: '', role: ManagementRole.MODERATOR, isActive: true });

  const currentMetrics = useMemo(() => calculateCurrentMetrics(vendors, ads, neighborhoods), [vendors, ads, neighborhoods]);

  const filteredVendors = useMemo(() => {
    return vendors.filter(v => {
      const matchesSearch = v.name.toLowerCase().includes(adminSearch.toLowerCase()) || v.whatsapp.includes(adminSearch);
      const matchesStatus = statusFilter === 'ALL' ? true : v.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [vendors, adminSearch, statusFilter]);

  const activeVendorForModal = useMemo(() => vendors.find(v => v.id === selectedVendorId) || null, [vendors, selectedVendorId]);
  const renewalVendor = useMemo(() => vendors.find(v => v.id === renewalVendorId) || null, [vendors, renewalVendorId]);
  const messageVendor = useMemo(() => vendors.find(v => v.id === messageVendorId) || null, [vendors, messageVendorId]);

  const EmptyDataMessage = ({ message }: { message: string }) => (
    <div className="flex flex-col items-center justify-center py-24 px-10 bg-white/5 rounded-[3rem] border border-white/5 text-center animate-in fade-in zoom-in duration-500">
      <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-6">
        <svg className="w-8 h-8 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
      </div>
      <p className="text-white/40 font-black uppercase text-xs tracking-[0.2em] italic">{message}</p>
    </div>
  );

  const handleChargePro = (vendor: VendorProfile) => {
    const expiresDate = vendor.plan_expires_at ? new Date(vendor.plan_expires_at).toLocaleDateString('pt-BR') : 'não definida';
    const message = `Olá, *${vendor.responsible}*! 👋 Tudo bem?\n\nSou da equipe de Sucesso do Cliente do *${systemConfig.marketplaceName}*.\n\nPassando para lembrar que o Plano PRO da sua loja (*${vendor.name}*) está próximo do vencimento em *${expiresDate}*.\n\nPara manter o seu *Selo Verificado*, anúncios ilimitados e o destaque prioritário na nossa vitrine local, gostaria de renovar por mais um período?\n\nFico à disposição para gerar o seu link de pagamento! 🚀`;
    window.open(`https://wa.me/${vendor.whatsapp}?text=${encodeURIComponent(message)}`, '_blank');
    showNotify("Canal de atendimento aberto no WhatsApp.");
  };

  const handleExtendPlan = (vendor: VendorProfile, days: number) => {
    const baseDate = vendor.plan_expires_at && new Date(vendor.plan_expires_at) > new Date() 
      ? new Date(vendor.plan_expires_at) 
      : new Date();
    
    const newDate = new Date(baseDate);
    newDate.setDate(newDate.getDate() + days);
    
    onUpdatePlan(vendor.id, SubscriptionPlan.PRO, newDate.toISOString());
    showNotify(`Plano estendido em ${days} dias com sucesso!`);
    setRenewalVendorId(null);
  };

  const handleSendRequestChanges = (message: string) => {
    if (requestVendor) {
      window.open(`https://wa.me/${requestVendor.whatsapp}?text=${encodeURIComponent(message)}`, '_blank');
      showNotify("Solicitação enviada via WhatsApp!");
      setRequestVendor(null);
      setSelectedVendorId(null);
    }
  };

  const renderKPIs = () => (
    <div className="animate-in fade-in duration-700 space-y-16 pb-20">
      <div className="flex justify-between items-end border-b border-white/5 pb-6">
        <div>
          <h2 className="text-4xl font-title font-black text-white uppercase italic tracking-tighter italic">Painel de Inteligência</h2>
          <p className="text-brand-blue text-[10px] font-black uppercase tracking-[0.2em] mt-2 flex items-center gap-2">
            <span className="w-1 h-3 bg-brand-orange"></span> Dashboard Completo de Guarulhos
          </p>
        </div>
        <div className="text-right">
          <p className="text-white/30 text-[9px] font-black uppercase tracking-widest">Última Atualização</p>
          <p className="text-white font-black text-xl leading-none mt-1">{currentTime}</p>
        </div>
      </div>

      <div className="space-y-6">
        <h4 className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-brand-orange"></span> 🔥 Sucesso & Conversão
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <MetricCard label="LGR (Conversão de Leads)" current={currentMetrics.lgr} previous={10.2} isPercentage description="Eficiência de cliques vs visualizações." />
          <MetricCard label="Anúncios Sem Click" current={currentMetrics.ads_no_clicks} previous={15} description="Anúncios que ainda não geraram contatos." />
          <MetricCard label="Ticket Médio Vitrine" current={currentMetrics.avg_price} previous={112.50} isCurrency description="Valor médio dos produtos ofertados." />
        </div>
      </div>

      <div className="space-y-6">
        <h4 className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500"></span> 🏥 Saúde da Rede & Retenção
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <MetricCard label="Parceiros Registrados" current={currentMetrics.total_vendors} previous={vendors.length > 0 ? vendors.length - 2 : 0} description="Crescimento líquido da base de lojistas." />
          <MetricCard label="Taxa de Atividade" current={currentMetrics.activity_rate} previous={45.5} isPercentage description="Parceiros com pelo menos 1 oferta ativa." />
          <MetricCard label="Índice de Churn" current={currentMetrics.churn_rate} previous={0} isPercentage description="Lojistas que removeram ofertas no período." />
        </div>
      </div>

      <div className="space-y-6">
        <h4 className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-brand-blue"></span> 📈 Escala & Receita
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <MetricCard label="Faturamento Estimado" current={currentMetrics.total_revenue} previous={890.00} isCurrency description="Receita recorrente dos assinantes PRO." />
          <MetricCard label="Assinantes PRO" current={currentMetrics.total_pro_vendors} previous={6} description="Conversão de parceiros grátis para pagos." />
          <MetricCard label="Receita Média (ARPU)" current={currentMetrics.arpu} previous={45.00} isCurrency description="Valor médio extraído de cada parceiro." />
        </div>
      </div>

      <div className="space-y-6">
        <h4 className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-white/20"></span> 📍 Geografia & Presença Comercial
        </h4>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 bg-white p-10 rounded-[3rem] shadow-2xl relative overflow-hidden flex flex-col justify-center min-h-[300px]">
            <div className="absolute top-10 right-10 text-gray-50 opacity-10">
              <svg className="w-40 h-40" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
            </div>
            <p className="text-[10px] font-black text-brand-navy uppercase tracking-widest mb-6">Bairro em Destaque</p>
            <h5 className="text-5xl font-title font-black text-brand-navy uppercase italic tracking-tighter leading-none mb-4 italic">{currentMetrics.featured_neighborhood}</h5>
            <div className="flex items-center gap-2">
               <span className="w-2 h-2 rounded-full bg-brand-orange"></span>
               <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Líder em Volume de Ofertas</p>
            </div>
          </div>

          <div className="lg:col-span-8 bg-white p-10 rounded-[3rem] shadow-2xl">
             <div className="flex justify-between items-center mb-10">
                <p className="text-[10px] font-black text-brand-navy uppercase tracking-widest">Top 5 Bairros (Volume de Anúncios)</p>
                <span className="bg-brand-blue/10 text-brand-blue text-[8px] font-black px-4 py-1.5 rounded-xl uppercase">Ranking Local</span>
             </div>
             <div className="space-y-8">
               {currentMetrics.top_5_neighborhoods.length > 0 ? currentMetrics.top_5_neighborhoods.map((n, idx) => (
                 <div key={n.name} className="space-y-2">
                   <div className="flex justify-between text-[9px] font-black uppercase">
                      <span className="text-brand-blue/40 italic mr-2">0{idx+1}. <span className="text-brand-navy font-black not-italic ml-1">{n.name}</span></span>
                      <span className="text-slate-400">{n.count} Ofertas</span>
                   </div>
                   <div className="h-2 w-full bg-gray-50 rounded-full overflow-hidden">
                      <div className="h-full bg-brand-navy rounded-full transition-all duration-1000 ease-out" style={{ width: `${(n.count / (currentMetrics.top_5_neighborhoods[0].count || 1)) * 100}%` }}></div>
                   </div>
                 </div>
               )) : (
                 <p className="text-center py-10 text-gray-300 font-black uppercase text-[10px]">Aguardando dados geográficos...</p>
               )}
             </div>
          </div>
        </div>
      </div>
    </div>
  );

  const getPromoStatus = (p: Promotion) => {
    const now = new Date();
    const start = p.start_date ? new Date(p.start_date) : null;
    const end = p.end_date ? new Date(p.end_date) : null;

    if (!p.is_active) return 'expired';
    if (start && start > now) return 'scheduled';
    if (end && end < now) return 'expired';
    return 'active';
  };

  const renderCampaigns = () => (
    <div className="animate-in fade-in duration-700 space-y-12 pb-20">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-4xl font-title font-black text-white uppercase italic tracking-tighter italic drop-shadow-lg">Centro de Campanhas</h2>
          <p className="text-brand-orange text-[10px] font-black uppercase tracking-[0.2em] mt-2 italic">Aumente o engajamento dos lojistas</p>
        </div>
        <button onClick={() => { setIsAddingPromo(true); setPromoWizardStep(1); }} className="bg-brand-orange text-white font-black text-[10px] px-10 py-5 rounded-[2.5rem] shadow-2xl uppercase tracking-[0.2em] hover:scale-105 transition-all">Nova Promoção</button>
      </div>

      {isAddingPromo ? (
        <div className="bg-white p-10 sm:p-14 rounded-[4rem] shadow-2xl border border-gray-100 animate-in slide-in-from-bottom-6 duration-500 max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <h3 className="text-2xl font-title font-black text-brand-navy uppercase italic">Wizard de Campanha</h3>
            <div className="flex gap-2">
              {[1, 2, 3].map(s => (
                <div key={s} className={`h-2 w-12 rounded-full transition-all ${promoWizardStep >= s ? 'bg-brand-orange' : 'bg-gray-100'}`}></div>
              ))}
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); if(promoWizardStep < 3) setPromoWizardStep(s => s + 1); else { onSavePromotion(newPromo); setIsAddingPromo(false); showNotify("Campanha Salva!"); } }}>
            {promoWizardStep === 1 && (
              <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Nome da Campanha</label>
                  <input required type="text" placeholder="Ex: Black Friday Guarulhos" className="w-full p-6 rounded-2xl bg-inputGray font-bold text-lg border-none" value={newPromo.name} onChange={e => setNewPromo({...newPromo, name: e.target.value})} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Regra de Ativação</label>
                    <select className="w-full p-6 rounded-2xl bg-inputGray font-black uppercase text-sm border-none appearance-none" value={newPromo.activation_rule} onChange={e => setNewPromo({...newPromo, activation_rule: e.target.value as any})}>
                      <option value={ActivationRule.NEW_SIGNUP}>Novos Cadastros</option>
                      <option value={ActivationRule.COUPON}>Código de Cupom</option>
                      <option value={ActivationRule.MANUAL}>Ativação Manual</option>
                    </select>
                  </div>
                  {newPromo.activation_rule === ActivationRule.COUPON && (
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Código do Cupom</label>
                      <input required type="text" placeholder="Ex: GRU6MESES" className="w-full p-6 rounded-2xl bg-inputGray font-black uppercase text-lg border-none" value={newPromo.coupon_code} onChange={e => setNewPromo({...newPromo, coupon_code: e.target.value.toUpperCase()})} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {promoWizardStep === 2 && (
              <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Benefício Principal</label>
                  <select className="w-full p-6 rounded-2xl bg-inputGray font-black uppercase text-sm border-none appearance-none" value={newPromo.benefit_type} onChange={e => setNewPromo({...newPromo, benefit_type: e.target.value as any})}>
                    <option value={PromotionType.PRO_UPGRADE}>Upgrade para Plano PRO</option>
                    <option value={PromotionType.VERIFIED_BADGE}>Conceder Selo Verificado</option>
                    <option value={PromotionType.EXTRA_ADS}>Anúncios Extras no Catálogo</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Valor do Benefício (Qtd ou Dias)</label>
                  <input required type="number" className="w-full p-6 rounded-2xl bg-inputGray font-black text-2xl border-none" value={newPromo.benefit_value} onChange={e => setNewPromo({...newPromo, benefit_value: parseInt(e.target.value)})} />
                  <p className="text-[9px] text-gray-400 uppercase font-black tracking-widest italic">{newPromo.benefit_type === PromotionType.EXTRA_ADS ? 'Número de produtos adicionais' : 'Duração do benefício em dias'}</p>
                </div>
              </div>
            )}

            {promoWizardStep === 3 && (
              <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Data de Início (Opcional)</label>
                    <input type="date" className="w-full p-6 rounded-2xl bg-inputGray font-bold border-none" value={newPromo.start_date} onChange={e => setNewPromo({...newPromo, start_date: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Data de Expiração (Opcional)</label>
                    <input type="date" className="w-full p-6 rounded-2xl bg-inputGray font-bold border-none" value={newPromo.end_date} onChange={e => setNewPromo({...newPromo, end_date: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Público Alvo (Categoria)</label>
                  <select className="w-full p-6 rounded-2xl bg-inputGray font-black uppercase text-sm border-none appearance-none" value={newPromo.category_id || ''} onChange={e => setNewPromo({...newPromo, category_id: e.target.value || undefined})}>
                    <option value="">Todas as Categorias</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                  </select>
                </div>
              </div>
            )}

            <div className="flex gap-4 mt-12 pt-8 border-t border-gray-50">
              {promoWizardStep > 1 && (
                <button type="button" onClick={() => setPromoWizardStep(s => s - 1)} className="flex-1 bg-gray-100 text-gray-400 font-black py-6 rounded-3xl uppercase tracking-widest text-[10px]">Voltar</button>
              )}
              <button type="button" onClick={() => setIsAddingPromo(false)} className="flex-1 bg-white border border-gray-100 text-gray-300 font-black py-6 rounded-3xl uppercase tracking-widest text-[10px]">Cancelar</button>
              <button type="submit" className="flex-[2] bg-brand-navy text-white font-black py-6 rounded-3xl uppercase tracking-widest text-[10px] shadow-xl hover:bg-brand-blue transition-all">
                {promoWizardStep < 3 ? 'Próximo Passo' : 'Lançar Campanha 🚀'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {promotions.map(p => {
            const status = getPromoStatus(p);
            const statusColors = {
              active: { badge: 'bg-green-100 text-green-700', border: 'border-brand-orange', label: '🟢 Rodando Agora' },
              scheduled: { badge: 'bg-yellow-100 text-yellow-700', border: 'border-brand-navy', label: '🟡 Agendada' },
              expired: { badge: 'bg-gray-100 text-gray-400', border: 'border-gray-200', label: '⚪ Encerrada' }
            }[status];

            return (
              <div key={p.id} className={`bg-white p-8 rounded-[3.5rem] shadow-2xl border-t-8 relative group transition-all hover:-translate-y-2 ${statusColors.border} ${status === 'expired' ? 'grayscale opacity-60' : ''}`}>
                 <div className="flex items-center justify-between mb-6">
                   <span className={`px-4 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest ${statusColors.badge}`}>
                     {statusColors.label}
                   </span>
                   <div className="bg-brand-navy/5 p-3 rounded-2xl text-brand-navy">
                      {p.benefit_type === PromotionType.PRO_UPGRADE ? '💎' : p.benefit_type === PromotionType.VERIFIED_BADGE ? '✅' : '📈'}
                   </div>
                 </div>
                 <h4 className="text-xl font-black text-brand-navy uppercase italic mb-2 tracking-tighter italic leading-none">{p.name}</h4>
                 <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6">
                   {p.activation_rule === ActivationRule.COUPON ? `CUPOM: ${p.coupon_code}` : 'Benefício Automático'}
                 </p>
                 
                 <div className="bg-gray-50 p-6 rounded-3xl space-y-3 mb-8">
                    <div className="flex justify-between items-center text-[9px] font-black uppercase">
                      <span className="text-gray-400">Benefício</span>
                      <span className="text-brand-navy">{p.benefit_value} {p.benefit_type === PromotionType.EXTRA_ADS ? 'Ads' : 'Dias'}</span>
                    </div>
                    {status === 'scheduled' && p.start_date && (
                      <div className="flex justify-between items-center text-[9px] font-black uppercase">
                        <span className="text-brand-navy">Inicia em</span>
                        <span className="text-brand-blue font-bold">{new Date(p.start_date).toLocaleDateString('pt-BR')}</span>
                      </div>
                    )}
                    {status === 'active' && p.end_date && (
                      <div className="flex justify-between items-center text-[9px] font-black uppercase">
                        <span className="text-gray-400">Vence em</span>
                        <span className="text-brand-orange font-bold">{new Date(p.end_date).toLocaleDateString('pt-BR')}</span>
                      </div>
                    )}
                 </div>

                 <button 
                   onClick={() => onSavePromotion({...p, is_active: !p.is_active})}
                   className={`w-full font-black py-4 rounded-2xl uppercase tracking-widest text-[9px] transition-all border ${p.is_active ? 'bg-white border-red-100 text-red-400 hover:bg-red-500 hover:text-white' : 'bg-green-500 text-white border-green-400 shadow-lg'}`}
                 >
                   {p.is_active ? 'Pausar Campanha' : 'Reativar Agora'}
                 </button>
              </div>
            );
          })}
          {promotions.length === 0 && (
            <div className="md:col-span-3">
              <EmptyDataMessage message="Nenhuma campanha criada. Comece agora para atrair mais parceiros!" />
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (tab) {
      case 'perfil':
        return (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-12">
            <h3 className="text-4xl font-title font-black text-white uppercase italic tracking-tighter italic drop-shadow-lg">Meu Perfil</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <form onSubmit={handleUpdateProfile} className="bg-white p-12 rounded-[4rem] shadow-2xl border border-gray-100 space-y-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-brand-blue/10 p-4 rounded-3xl text-brand-blue">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7 7z" /></svg>
                  </div>
                  <h4 className="text-xl font-title font-black text-brand-navy uppercase italic tracking-tighter italic">Dados do Gestor</h4>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1 mb-2">E-mail (Identificador)</label>
                    <div className="p-5 rounded-2xl bg-gray-100 text-gray-400 font-bold border border-gray-200">{me?.email}</div>
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1 mb-2">Nome Completo</label>
                    <input type="text" className="w-full p-5 rounded-2xl bg-inputGray text-brand-navy font-bold border-none" value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1 mb-2">WhatsApp de Contato</label>
                    <input type="tel" className="w-full p-5 rounded-2xl bg-inputGray text-brand-navy font-bold border-none" value={profileForm.whatsapp} onChange={e => setProfileForm({...profileForm, whatsapp: e.target.value})} />
                  </div>
                </div>
                
                <button disabled={isSaving} type="submit" className="w-full bg-brand-navy text-white font-black py-6 rounded-3xl uppercase tracking-widest text-xs hover:bg-brand-blue transition-all shadow-xl flex items-center justify-center gap-3">
                  {isSaving && (
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {isSaving ? 'Salvando...' : 'Salvar Dados Cadastrais'}
                </button>
              </form>

              <form onSubmit={handleUpdatePassword} className="bg-white p-12 rounded-[4rem] shadow-2xl border border-gray-100 space-y-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-brand-orange/10 p-4 rounded-3xl text-brand-orange">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  </div>
                  <h4 className="text-xl font-title font-black text-brand-navy uppercase italic tracking-tighter italic">Segurança</h4>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1 mb-2">Senha Atual</label>
                    <input type="password" placeholder="••••••••" className="w-full p-5 rounded-2xl bg-inputGray text-brand-navy font-bold border-none" value={passwordForm.current} onChange={e => setPasswordForm({...passwordForm, current: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1 mb-2">Nova Senha</label>
                    <input type="password" placeholder="••••••••" className="w-full p-5 rounded-2xl bg-inputGray text-brand-navy font-bold border-none" value={passwordForm.new} onChange={e => setPasswordForm({...passwordForm, new: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block ml-1 mb-2">Confirmar Nova Senha</label>
                    <input type="password" placeholder="••••••••" className="w-full p-5 rounded-2xl bg-inputGray text-brand-navy font-bold border-none" value={passwordForm.confirm} onChange={e => setPasswordForm({...passwordForm, confirm: e.target.value})} />
                  </div>
                </div>
                
                <button disabled={isSaving || !passwordForm.new} type="submit" className="w-full bg-brand-orange text-white font-black py-6 rounded-3xl uppercase tracking-widest text-xs hover:bg-brand-navy transition-all shadow-xl flex items-center justify-center gap-3">
                  {isSaving && (
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {isSaving ? 'Processando...' : 'Alterar Senha de Acesso'}
                </button>
              </form>
            </div>
          </div>
        );
      case 'kpis':
        return renderKPIs();
      case 'vendedores':
        return (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <h3 className="text-4xl font-title font-black text-white uppercase italic tracking-tighter italic drop-shadow-lg">Gestão de Parceiros</h3>
              <div className="flex gap-3 w-full md:w-auto">
                 <input 
                  type="text" 
                  placeholder="Loja ou WhatsApp..." 
                  className="bg-white/10 border-none text-white placeholder-white/30 text-lg font-bold p-6 rounded-2xl flex-1 md:w-80 backdrop-blur-md outline-none focus:ring-2 focus:ring-brand-orange" 
                  value={adminSearch} 
                  onChange={e => setAdminSearch(e.target.value)} 
                 />
                 <select className="bg-white/10 border-none text-white font-black uppercase p-6 rounded-2xl backdrop-blur-md outline-none text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)}>
                    <option value="ALL" className="bg-brand-navy">Todos Status</option>
                    <option value={VendorStatus.PENDING} className="bg-brand-navy">Pendentes</option>
                    <option value={VendorStatus.APPROVED} className="bg-brand-navy">Operantes</option>
                    <option value={VendorStatus.REJECTED} className="bg-brand-navy">Pausados</option>
                 </select>
              </div>
            </div>

            {filteredVendors.length > 0 ? (
              <div className="bg-white/5 rounded-[3rem] overflow-hidden border border-white/10 backdrop-blur-xl shadow-2xl">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-white/5 text-[10px] font-black text-white/40 uppercase tracking-[0.3em]">
                      <th className="px-10 py-8">Parceiro</th>
                      <th className="px-10 py-8">Bairro</th>
                      <th className="px-10 py-8">Status</th>
                      <th className="px-10 py-8">Plano</th>
                      <th className="px-10 py-8 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredVendors.map(v => (
                      <tr key={v.id} className="hover:bg-white/5 transition-all group">
                        <td className="px-10 py-8">
                          <div className="flex items-center gap-5">
                            <img src={v.avatar_url} className="w-14 h-14 rounded-2xl object-cover shadow-lg border-2 border-white/10" />
                            <div>
                              <p className="text-white font-black text-base uppercase italic leading-none">{v.name}</p>
                              <p className="text-white/30 text-[9px] mt-2 uppercase font-black tracking-widest">{v.responsible}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-10 py-8 text-white/50 text-[10px] font-black uppercase tracking-widest">{neighborhoods.find(n => n.id === v.neighborhood_id)?.name}</td>
                        <td className="px-10 py-8">
                           <span className={`px-4 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest ${v.status === 'approved' ? 'bg-green-500 text-white shadow-lg shadow-green-500/20' : v.status === 'pending' ? 'bg-brand-orange text-white animate-pulse' : 'bg-red-500 text-white'}`}>
                             {v.status === 'approved' ? 'Operante ✓' : v.status === 'pending' ? 'Em Análise' : 'Pausado'}
                           </span>
                        </td>
                        <td className="px-10 py-8 text-white/50 text-[10px] font-black uppercase tracking-widest">{v.plan}</td>
                        <td className="px-10 py-8 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <button 
                              onClick={() => setMessageVendorId(v.id)}
                              className="p-3 bg-white/5 text-white/40 hover:text-white hover:bg-white/10 rounded-xl transition-all"
                              title="Mensagem Rápida"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/></svg>
                            </button>
                            <button onClick={() => setSelectedVendorId(v.id)} className="text-[10px] font-black text-brand-blue uppercase hover:text-white transition-all bg-brand-blue/10 px-6 py-3 rounded-xl hover:bg-brand-blue">Ver Detalhes →</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyDataMessage message="Nenhum parceiro encontrado nesta categoria." />
            )}
          </div>
        );
      case 'campanhas':
        return renderCampaigns();
      case 'renovacoes':
        const proVendors = vendors.filter(v => v.plan === SubscriptionPlan.PRO);
        return (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-10">
            <h3 className="text-4xl font-title font-black text-white uppercase italic tracking-tighter italic drop-shadow-lg">Monitor de Planos PRO</h3>
            {proVendors.length > 0 ? (
              <div className="grid grid-cols-1 gap-6">
                {proVendors.map(v => (
                  <div key={v.id} className="bg-white/5 p-10 rounded-[3rem] flex flex-col sm:flex-row items-center justify-between border border-white/10 backdrop-blur-md gap-8">
                    <div className="flex items-center gap-8 w-full sm:w-auto">
                      <img src={v.avatar_url} className="w-20 h-20 rounded-[2rem] object-cover shadow-2xl border-2 border-brand-orange/30" />
                      <div>
                        <h4 className="text-white font-black uppercase italic text-2xl leading-none mb-3 tracking-tighter">{v.name}</h4>
                        <div className="flex flex-col gap-1">
                          <label className="text-[8px] font-black text-white/30 uppercase tracking-widest">Data de Vencimento</label>
                          <p className="text-brand-orange font-black text-base uppercase italic tracking-widest">
                            {v.plan_expires_at ? new Date(v.plan_expires_at).toLocaleDateString('pt-BR') : 'Não Definida'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 w-full sm:w-auto justify-end">
                      <button 
                        onClick={() => handleChargePro(v)}
                        className="bg-white/5 border border-white/10 text-white font-black px-8 py-5 rounded-[2rem] text-[10px] uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-2"
                      >
                        <svg className="w-4 h-4 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.937 3.659 1.432 5.632 1.433h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        Cobrar via WhatsApp
                      </button>
                      <button 
                        onClick={() => setRenewalVendorId(v.id)}
                        className="bg-brand-orange text-white font-black px-8 py-5 rounded-[2rem] text-[10px] uppercase tracking-widest shadow-xl shadow-brand-orange/20 hover:scale-105 active:scale-95 transition-all"
                      >
                        Estender Validez
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyDataMessage message="Atualmente não existem assinantes PRO ativos." />
            )}
          </div>
        );
      case 'equipe':
        return (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-10">
             <div className="flex items-center justify-between">
                <h3 className="text-4xl font-title font-black text-white uppercase italic tracking-tighter italic drop-shadow-lg">Conselho Gestor</h3>
                <button onClick={() => setIsStaffModalOpen(true)} className="bg-brand-orange text-white font-black text-[10px] px-10 py-5 rounded-[2rem] shadow-2xl uppercase tracking-[0.2em] hover:scale-105 transition-all">Novo Moderador</button>
             </div>
             {staffList.length > 0 ? (
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                 {staffList.map(member => (
                   <div key={member.id} className={`bg-white p-10 rounded-[4rem] shadow-2xl relative group border-t-8 ${member.role === 'super_admin' ? 'border-brand-orange' : 'border-brand-blue'} ${!member.isActive ? 'opacity-40 grayscale' : ''}`}>
                      <div className="flex items-center justify-between mb-8">
                         <span className={`px-5 py-2 rounded-2xl text-[9px] font-black uppercase tracking-widest ${member.role === ManagementRole.SUPER_ADMIN ? 'bg-brand-orange text-white' : 'bg-brand-blue text-white'}`}>
                           {member.role === ManagementRole.SUPER_ADMIN ? '★ Master Admin' : member.role}
                         </span>
                      </div>
                      <h4 className="text-2xl font-black text-brand-navy uppercase italic mb-2 tracking-tighter italic">{member.name}</h4>
                      <p className="text-[10px] font-bold text-slate-400 lowercase tracking-widest mb-6">{member.email}</p>
                   </div>
                 ))}
               </div>
             ) : (
               <EmptyDataMessage message="Nenhum moderador cadastrado no sistema." />
             )}
          </div>
        );
      case 'taxonomias':
        return (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 grid grid-cols-1 lg:grid-cols-12 gap-12 pb-20 items-start">
             <div className="lg:col-span-6 bg-white/5 p-10 rounded-[3.5rem] border border-white/10 backdrop-blur-xl shadow-2xl overflow-hidden">
               <h3 className="text-xl font-title font-black text-white uppercase italic mb-8 tracking-tighter italic">Bairros Estratégicos</h3>
               <div className="flex flex-col sm:flex-row gap-3 mb-10 items-stretch">
                  <input type="text" placeholder="Nome do Bairro..." className="bg-white border-none text-brand-navy placeholder-slate-400 text-sm font-bold p-5 rounded-2xl flex-1 outline-none focus:ring-4 focus:ring-brand-orange/20" value={newNeighName} onChange={e => setNewNeighName(e.target.value)} />
                  <button onClick={() => { if(!newNeighName) return; onAddNeighborhood(newNeighName, isAirport); showNotify(`Bairro ${newNeighName} adicionado!`); setNewNeighName(''); }} className="bg-brand-orange text-white font-black px-10 py-5 rounded-2xl text-[10px] uppercase tracking-[0.2em] shadow-lg shadow-brand-orange/20 hover:scale-105 active:scale-95 transition-all">ADD</button>
               </div>
               <div className="space-y-3 max-h-[500px] overflow-y-auto no-scrollbar pr-1">
                  {neighborhoods.length > 0 ? neighborhoods.map(n => (
                    <div key={n.id} className="bg-white/5 p-5 rounded-2xl text-white/70 text-[10px] font-black uppercase tracking-[0.2em] flex justify-between items-center group hover:bg-white/10 transition-all border border-transparent hover:border-white/5 h-16">
                      {editingNeighId === n.id ? (
                        <div className="flex items-center gap-3 w-full animate-in fade-in zoom-in duration-200">
                          <input 
                            type="text" 
                            className="bg-white border-none rounded-xl px-4 py-2.5 text-brand-navy text-xs font-bold flex-1"
                            value={neighEditName}
                            onChange={e => setNeighEditName(e.target.value)}
                          />
                          <button onClick={() => { onUpdateNeighborhood(n.id, neighEditName, n.is_airport_zone); setEditingNeighId(null); showNotify("Bairro atualizado!"); }} className="bg-green-500 text-white p-2.5 rounded-xl shadow-lg">✓</button>
                          <button onClick={() => setEditingNeighId(null)} className="bg-white/10 text-white p-2.5 rounded-xl hover:bg-red-500 transition-all">✕</button>
                        </div>
                      ) : (
                        <>
                          <span className="flex items-center gap-2">📍 {n.name}</span>
                          <button 
                            onClick={() => { setEditingNeighId(n.id); setNeighEditName(n.name); }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/10 p-2.5 rounded-xl hover:bg-brand-blue text-white"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                          </button>
                        </>
                      )}
                    </div>
                  )) : (
                    <p className="text-center py-10 text-white/20 font-black uppercase text-[10px] italic tracking-widest">Nenhum bairro cadastrado.</p>
                  )}
               </div>
             </div>

             <div className="lg:col-span-6 bg-white/5 p-10 rounded-[3.5rem] border border-white/10 backdrop-blur-xl shadow-2xl overflow-hidden">
               <h3 className="text-xl font-title font-black text-white uppercase italic mb-8 tracking-tighter italic">Segmentos de Negócio</h3>
               <div className="flex flex-col sm:flex-row gap-3 mb-10 items-stretch">
                  <div className="w-20 bg-white rounded-2xl flex items-center justify-center shadow-inner shrink-0 h-[60px]">
                    <input 
                      type="text" 
                      maxLength={2}
                      placeholder="Icon" 
                      className="w-full bg-transparent border-none text-center text-xl outline-none p-0 h-full" 
                      value={newCatIcon} 
                      onChange={e => setNewCatIcon(e.target.value)} 
                    />
                  </div>
                  <input 
                    type="text" 
                    placeholder="Nome da Categoria..." 
                    className="bg-white border-none text-brand-navy placeholder-slate-400 text-sm font-bold p-5 rounded-2xl flex-1 outline-none focus:ring-4 focus:ring-brand-orange/20 h-[60px]" 
                    value={newCatName} 
                    onChange={e => setNewCatName(e.target.value)} 
                  />
                  <button onClick={() => { if(!newCatName) return; onAddCategory(newCatName, newCatIcon); showNotify(`Categoria ${newCatName} adicionada!`); setNewCatName(''); }} className="bg-brand-orange text-white font-black px-10 rounded-2xl text-[10px] uppercase tracking-[0.2em] shadow-lg shadow-brand-orange/20 hover:scale-105 active:scale-95 transition-all h-[60px]">ADD</button>
               </div>

               <div className="space-y-3 max-h-[500px] overflow-y-auto no-scrollbar pr-1">
                  {categories.length > 0 ? categories.map(c => (
                    <div key={c.id} className="bg-white/5 p-5 rounded-2xl text-white/70 text-[10px] font-black uppercase tracking-[0.2em] flex justify-between items-center group hover:bg-white/10 transition-all border border-transparent hover:border-white/5 h-16">
                      {editingCatId === c.id ? (
                        <div className="flex items-center gap-3 w-full animate-in fade-in zoom-in duration-200">
                          <input 
                            type="text" 
                            className="bg-white border-none rounded-xl px-2 py-2.5 text-brand-navy text-center w-12 font-bold"
                            value={catEditIcon}
                            onChange={e => setCatEditIcon(e.target.value)}
                          />
                          <input 
                            type="text" 
                            className="bg-white border-none rounded-xl px-4 py-2.5 text-brand-navy text-xs font-bold flex-1"
                            value={catEditName}
                            onChange={e => setCatEditName(e.target.value)}
                          />
                          <button onClick={() => { onUpdateCategory(c.id, catEditName, catEditIcon); setEditingCatId(null); showNotify("Categoria atualizada!"); }} className="bg-green-500 text-white p-2.5 rounded-xl shadow-lg">✓</button>
                          <button onClick={() => setEditingCatId(null)} className="bg-white/10 text-white p-2.5 rounded-xl hover:bg-red-500 transition-all">✕</button>
                        </div>
                      ) : (
                        <>
                          <span className="flex items-center gap-4"><span className="text-2xl drop-shadow-sm grayscale group-hover:grayscale-0 transition-all">{c.icon || '📁'}</span> {c.name}</span>
                          <button 
                            onClick={() => { setEditingCatId(c.id); setCatEditName(c.name); setCatEditIcon(c.icon || '📁'); }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/10 p-2.5 rounded-xl hover:bg-brand-orange text-white"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/></svg>
                          </button>
                        </>
                      )}
                    </div>
                  )) : (
                    <p className="text-center py-10 text-white/20 font-black uppercase text-[10px] italic tracking-widest">Nenhuma categoria cadastrada.</p>
                  )}
               </div>
             </div>
          </div>
        );
      case 'config':
        return (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-12 pb-20">
             <div className="bg-white/5 p-16 rounded-[4rem] border border-white/10 backdrop-blur-xl shadow-2xl">
                <h3 className="text-3xl font-title font-black text-white uppercase italic mb-12 tracking-tighter italic">Sistema de Branding & Social</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                   <div className="space-y-6">
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] block ml-1">Nome do Marketplace</label>
                         <input type="text" className="bg-white border-none text-brand-navy font-black text-lg p-6 rounded-3xl w-full outline-none focus:ring-2 focus:ring-brand-orange" value={systemConfig.marketplaceName} onChange={e => onUpdateConfig({...systemConfig, marketplaceName: e.target.value})} />
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] block ml-1">Descrição para o Rodapé</label>
                         <textarea rows={4} className="bg-white border-none text-brand-navy font-bold text-sm p-6 rounded-3xl w-full outline-none focus:ring-2 focus:ring-brand-orange resize-none" value={systemConfig.marketplaceDescription} onChange={e => onUpdateConfig({...systemConfig, marketplaceDescription: e.target.value})} />
                      </div>
                   </div>
                   <div className="space-y-6">
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] block ml-1">Instagram URL</label>
                         <div className="relative">
                            <input type="text" className="bg-white border-none text-brand-navy font-black text-lg p-6 pl-14 rounded-3xl w-full outline-none focus:ring-2 focus:ring-brand-orange" value={systemConfig.instagramUrl} onChange={e => onUpdateConfig({...systemConfig, instagramUrl: e.target.value})} />
                            <span className="absolute left-6 top-1/2 -translate-y-1/2 text-brand-navy/20">📸</span>
                         </div>
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] block ml-1">YouTube URL</label>
                         <div className="relative">
                            <input type="text" className="bg-white border-none text-brand-navy font-black text-lg p-6 pl-14 rounded-3xl w-full outline-none focus:ring-2 focus:ring-brand-orange" value={systemConfig.youtubeUrl} onChange={e => onUpdateConfig({...systemConfig, youtubeUrl: e.target.value})} />
                            <span className="absolute left-6 top-1/2 -translate-y-1/2 text-brand-navy/20">🎥</span>
                         </div>
                      </div>
                      <div className="space-y-2">
                         <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] block ml-1">WhatsApp Suporte Central</label>
                         <input type="text" className="bg-white border-none text-brand-navy font-black text-lg p-6 rounded-3xl w-full outline-none focus:ring-2 focus:ring-brand-orange" value={systemConfig.supportWhatsapp} onChange={e => onUpdateConfig({...systemConfig, supportWhatsapp: e.target.value})} />
                      </div>
                   </div>
                </div>
             </div>

             <div className="bg-white/5 p-16 rounded-[4rem] border border-white/10 backdrop-blur-xl shadow-2xl">
                <h3 className="text-3xl font-title font-black text-white uppercase italic mb-12 tracking-tighter italic">Limites de Operação</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                   <div className="space-y-2">
                      <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] block ml-1">Anúncios Máximos (Grátis)</label>
                      <input type="number" className="bg-white border-none text-brand-navy font-black text-4xl p-10 rounded-[2.5rem] w-full outline-none focus:ring-2 focus:ring-brand-orange shadow-inner" value={systemConfig.maxAdsFree} onChange={e => onUpdateConfig({...systemConfig, maxAdsFree: parseInt(e.target.value)})} />
                      <p className="text-[9px] text-white/20 uppercase font-black tracking-widest mt-3 ml-2 italic">Limita novos cadastros orgânicos</p>
                   </div>
                   <div className="space-y-2">
                      <label className="text-[10px] font-black text-white/40 uppercase tracking-[0.3em] block ml-1">Anúncios Máximos (PRO)</label>
                      <input type="number" className="bg-white border-none text-brand-navy font-black text-4xl p-10 rounded-[2.5rem] w-full outline-none focus:ring-2 focus:ring-brand-orange shadow-inner" value={systemConfig.maxAdsPro} onChange={e => onUpdateConfig({...systemConfig, maxAdsPro: parseInt(e.target.value)})} />
                      <p className="text-[9px] text-white/20 uppercase font-black tracking-widest mt-3 ml-2 italic">Capacidade máxima de parceiros pagos</p>
                   </div>
                </div>
                <button 
                  onClick={() => { onSaveConfig(); showNotify("Configurações do sistema aplicadas!"); }}
                  className="w-full bg-brand-orange text-white font-black py-8 rounded-[3rem] uppercase text-xs tracking-[0.4em] shadow-2xl shadow-brand-orange/20 hover:scale-[1.01] active:scale-95 transition-all mt-14"
                >
                   Efetivar Configurações de Produção
                </button>
             </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-brand-navy font-sans relative flex flex-col overflow-hidden">
      {notification && (
        <div className="fixed top-8 left-1/2 -translate-x-1/2 z-[400] animate-in slide-in-from-top-4 duration-300">
          <div className={`px-8 py-5 rounded-[2rem] shadow-2xl flex items-center gap-4 border backdrop-blur-xl ${notification.type === 'success' ? 'bg-green-500/90 border-green-400 text-white' : 'bg-red-500/90 border-red-400 text-white'}`}>
             <span className="text-sm font-black uppercase tracking-widest">{notification.message}</span>
          </div>
        </div>
      )}

      {renewalVendor && (
        <div className="fixed inset-0 z-[350] flex items-center justify-center p-6 backdrop-blur-2xl">
          <div className="absolute inset-0 bg-navy/95" onClick={() => setRenewalVendorId(null)} />
          <div className="relative bg-white w-full max-w-md rounded-[4rem] p-12 shadow-2xl border border-white/10 animate-in zoom-in duration-300 text-center">
            <div className="w-20 h-20 bg-brand-orange/10 rounded-full flex items-center justify-center mx-auto mb-8">
               <svg className="w-10 h-10 text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            </div>
            <h3 className="text-2xl font-title font-black text-brand-navy uppercase italic mb-2 tracking-tighter leading-none italic">Estender Plano PRO</h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-10">Lojista: {renewalVendor.name}</p>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
               {[15, 30, 60, 90].map(days => (
                 <button 
                  key={days}
                  onClick={() => handleExtendPlan(renewalVendor, days)}
                  className="bg-inputGray hover:bg-brand-blue hover:text-white p-6 rounded-[2rem] font-black transition-all group"
                 >
                    <span className="block text-2xl group-hover:scale-110 transition-transform">+{days}</span>
                    <span className="text-[8px] uppercase tracking-widest opacity-50">Dias Corridos</span>
                 </button>
               ))}
            </div>

            <div className="mb-8">
               <button 
                onClick={() => handleExtendPlan(renewalVendor, 180)}
                className="w-full bg-brand-navy border-2 border-brand-orange text-white hover:bg-brand-orange p-6 rounded-[2rem] font-black transition-all group relative overflow-hidden"
               >
                  <div className="absolute top-0 right-0 bg-brand-orange text-white text-[7px] font-black uppercase tracking-tighter rounded-bl-xl group-hover:bg-brand-navy transition-colors">🔥 Benefício Pioneiro</div>
                  <span className="block text-2xl group-hover:scale-110 transition-transform">+180 Dias</span>
                  <span className="text-[8px] uppercase tracking-widest opacity-70">(6 Meses PRO Grátis)</span>
               </button>
            </div>
            
            <button onClick={() => setRenewalVendorId(null)} className="w-full text-[10px] font-black text-gray-300 uppercase tracking-widest hover:text-red-500 transition-colors py-2">Cancelar Operação</button>
          </div>
        </div>
      )}

      {requestVendor && (
        <RequestChangesModal 
          vendor={requestVendor}
          onClose={() => setRequestVendor(null)}
          onSend={handleSendRequestChanges}
        />
      )}

      {messageVendor && (
        <QuickMessageModal 
          vendor={messageVendor}
          onClose={() => setMessageVendorId(null)}
        />
      )}

      <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-brand-blue/10 blur-[200px] rounded-full -mr-40 -mt-40 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-brand-orange/5 blur-[150px] rounded-full -ml-32 -mb-32 pointer-events-none"></div>

      {isStaffModalOpen && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6 backdrop-blur-2xl">
          <div className="absolute inset-0 bg-navy/95" onClick={() => setIsStaffModalOpen(false)} />
          <div className="relative bg-white w-full max-md rounded-[4rem] p-12 shadow-2xl border border-white/10 animate-in zoom-in duration-300">
            <h3 className="text-3xl font-title font-black text-brand-navy uppercase italic mb-10 tracking-tighter italic leading-none">Novo Moderador</h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const { error } = await supabase.from('equipe_gestao').insert({
                nome: staffForm.name,
                email: staffForm.email,
                whatsapp: staffForm.whatsapp,
                password: staffForm.password,
                papel: staffForm.role,
                is_active: true
              });
              if (!error) {
                showNotify("Novo moderador registrado com sucesso!");
                onUpdateStaff();
                setIsStaffModalOpen(false);
              } else {
                showNotify("Erro ao cadastrar moderador", "error");
              }
            }} className="space-y-6">
              <input required type="text" placeholder="Nome Completo" className="w-full p-6 rounded-2xl bg-inputGray text-brand-navy font-bold text-lg border border-gray-100" value={staffForm.name} onChange={e => setStaffForm({...staffForm, name: e.target.value})} />
              <input required type="email" placeholder="E-mail" className="w-full p-6 rounded-2xl bg-inputGray text-brand-navy font-bold text-lg border border-gray-100" value={staffForm.email} onChange={e => setStaffForm({...staffForm, email: e.target.value})} />
              <input required type="password" placeholder="Senha Temporária" className="w-full p-6 rounded-2xl bg-inputGray text-brand-navy font-bold text-lg border border-gray-100" value={staffForm.password} onChange={e => setStaffForm({...staffForm, password: e.target.value})} />
              <select className="w-full p-6 rounded-2xl bg-inputGray text-brand-navy font-black uppercase text-sm tracking-widest border border-gray-100" value={staffForm.role} onChange={e => setStaffForm({...staffForm, role: e.target.value as any})}>
                <option value={ManagementRole.MODERATOR}>Moderador</option>
                <option value={ManagementRole.ADMIN}>Administrador Pleno</option>
              </select>
              <button type="submit" className="w-full bg-brand-navy text-white font-black py-7 rounded-3xl uppercase tracking-widest text-[11px] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all">Cadastrar Moderador</button>
            </form>
          </div>
        </div>
      )}

      {activeVendorForModal && (
        <VendorPreviewModal 
          vendor={activeVendorForModal}
          neighborhood={neighborhoods.find(n => n.id === activeVendorForModal.neighborhood_id)}
          category={categories.find(c => c.id === activeVendorForModal.category_id)}
          categories={categories}
          onClose={() => setSelectedVendorId(null)}
          onApprove={(id) => { onApprove(id); showNotify("Parceiro aprovado!"); }}
          onReject={(id) => { onReject(id); showNotify("Parceiro pausado.", "error"); }}
          onRequestChange={(v) => { setRequestVendor(v); }}
          onUpdatePlan={(id, plan, daysOrDate) => { onUpdatePlan(id, plan, daysOrDate); showNotify(`Vencimento atualizado.`); }}
          onGrantBadge={(days) => { onUpdateVerificationBadge(activeVendorForModal.id, days); showNotify(days > 0 ? `Selo concedido por ${days} dias` : "Selo removido"); }}
          onUpdateCategory={(vId, cId) => { onUpdateVendorCategory(vId, cId); showNotify("Categoria do lojista alterada!"); }}
        />
      )}

      <header className="bg-white/5 backdrop-blur-md h-24 flex items-center justify-between px-10 shrink-0 z-50 border-b border-white/5">
        <div className="flex items-center gap-4">
           <Logo className="h-12" />
           <span className="h-6 w-px bg-white/10 hidden sm:block"></span>
           <span className="text-white/30 text-[9px] font-black uppercase tracking-[0.3em] hidden sm:block italic">Painel de Controle</span>
        </div>
        <div className="flex items-center gap-6">
            <div onClick={() => setTab('perfil')} className="hidden sm:flex flex-col text-right cursor-pointer group">
               <span className="text-[8px] font-black text-white/30 uppercase tracking-[0.4em] mb-1 leading-none group-hover:text-brand-orange transition-colors">Gestão Master</span>
               <span className="text-xs font-black text-white uppercase italic leading-none group-hover:text-white transition-all">{me?.name || managementRole}</span>
            </div>
            <button onClick={onBack} className="bg-red-500/10 text-red-500 px-8 py-4 rounded-2xl hover:bg-red-500 hover:text-white transition-all font-black text-[10px] uppercase tracking-widest shadow-lg border border-red-500/20">Finalizar Sessão</button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <aside className="w-72 shrink-0 flex flex-col pt-12 px-6 border-r border-white/5 bg-navy/20">
          <nav className="flex-1 space-y-3">
            {[
              { id: 'kpis', label: 'Monitoramento BI' },
              { id: 'vendedores', label: 'Parceiros Locais' },
              { id: 'campanhas', label: 'Centro de Campanhas' },
              { id: 'renovacoes', label: 'Planos & Caixa' },
              { id: 'equipe', label: 'Gestão Interna', restricted: true },
              { id: 'taxonomias', label: 'Cidade & Categorias' },
              { id: 'config', label: 'Configurações' },
              { id: 'perfil', label: 'Meu Perfil', separator: true }
            ].map(menuItem => {
              if (menuItem.restricted && managementRole !== ManagementRole.SUPER_ADMIN) return null;
              return (
                <div key={menuItem.id}>
                  {menuItem.separator && <div className="h-px bg-white/5 my-6 mx-4"></div>}
                  <button 
                    onClick={() => setTab(menuItem.id as any)} 
                    className={`w-full text-left px-8 py-5 rounded-3xl text-[10px] font-black uppercase tracking-widest transition-all ${tab === menuItem.id ? 'bg-brand-blue text-white shadow-2xl scale-105 border border-white/10' : 'text-white/20 hover:text-white hover:bg-white/5'}`}
                  >
                    {menuItem.label}
                  </button>
                </div>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 overflow-y-auto p-12 no-scrollbar">
           {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
